import {
  type ComponentType,
  type KeyboardEvent,
  useCallback,
  useMemo
} from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon } from '@elfsight/lib-icon';
import { ellipsisMixin } from '@elfsight/widget-ui';
import type { A11yState, Setting } from '../../types';
import { useA11yState } from '../hooks/use-a11y-state';
import { Radio } from './radio';
import { ToggleSwitch } from './toggle-switch';

export type ItemType =
  | { type: 'switch' }
  | { type: 'radio'; radioSettingName: keyof A11yState };

type SettingItemProps = ItemType & {
  label: string;
  icon: ComponentType;
  settingName: Setting;
};

export function SettingItem(props: SettingItemProps) {
  const { label, icon, type, settingName } = props;
  const theme = useTheme();
  const { a11yState, setA11yState } = useA11yState();

  const labelId = `${settingName}-label`;
  const isRadioType = type === 'radio';
  const radioSettingName = isRadioType ? props.radioSettingName : undefined;

  const active = useMemo(() => {
    if (!a11yState) {
      return false;
    }

    if (radioSettingName) {
      return settingName === a11yState[radioSettingName];
    }

    return Boolean(a11yState[settingName as keyof A11yState]);
  }, [a11yState, settingName, radioSettingName]);

  const handleClick = useCallback(() => {
    if (isRadioType) {
      setA11yState({
        [radioSettingName!]: settingName
      });

      return;
    }

    setA11yState({
      [settingName]: !active
    });
  }, [active, isRadioType, settingName, setA11yState, radioSettingName]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const renderModeComponent = useMemo(
    () =>
      ({
        radio: <Radio active={active} />,
        switch: <ToggleSwitch active={active} />
      })[type],
    [active, type]
  );

  return (
    <Container
      aria-checked={active}
      role={type}
      aria-labelledby={labelId}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Icon component={icon} size={24} fill={theme.settingItem.iconColor} />
      <Label id={labelId}>{label}</Label>
      {renderModeComponent}
    </Container>
  );
}

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 12px;
    border-radius: 100px;
    cursor: pointer;
    transition: background-color 0.15s;
    background-color: ${theme.settingItem.backgroundColor};

    &:hover {
      background-color: ${theme.settingItem.hoverBackgroundColor};
    }

    &:focus-visible {
      background-color: ${theme.settingItem.hoverBackgroundColor};
      outline: 3px solid ${theme.settingItem.outlineColor};
      outline-offset: 2px;
      z-index: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

const Label = styled.div`
  ${({ theme }) => css`
    flex: 1;
    color: ${theme.settingItem.labelColor};
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;

    ${ellipsisMixin}
  `}
`;
